import React from "react";
import { Smartphone } from "lucide-react";
import { ASSETS } from "@/lib/assets";

const SCREENS = [
  {
    src: ASSETS.openScreen,
    title: "Splash & Login",
    desc: "Go FinX, Go Alpha — Fifi greets you on launch.",
    accent: "from-[#EEF2FF] via-[#FAE8FF] to-[#FFEDD5]",
    chip: "Onboarding",
    chipClass: "bg-white/80 text-[#5B21B6]",
  },
  {
    src: ASSETS.assetScreen,
    title: "Asset Dashboard",
    desc: "Total assets, buying power & portfolio donut in one view.",
    accent: "from-[#DBEAFE] via-[#FAE8FF] to-[#FFEDD5]",
    chip: "Home",
    chipClass: "bg-white/80 text-[#1E40AF]",
  },
  {
    src: ASSETS.marketScreen,
    title: "Markets",
    desc: "QQQ, SPY, DIA pulse cards + insider transactions & flow.",
    accent: "from-[#FCE7F3] via-[#EDE9FE] to-[#DBEAFE]",
    chip: "Live Data",
    chipClass: "bg-white/80 text-[#9D174D]",
  },
  {
    src: ASSETS.orderComplete,
    title: "Order Complete",
    desc: "Fifi cheers for every fill. Confetti, paws, and receipts.",
    accent: "from-[#EDE9FE] via-[#F5F3FF] to-[#FCE7F3]",
    chip: "Trade Flow",
    chipClass: "bg-white/80 text-[#5B21B6]",
  },
];

const PhoneFrame = ({ children, accent }) => (
  <div className="relative">
    <div
      className={`absolute -inset-6 bg-gradient-to-br ${accent} rounded-[3rem] blur-2xl opacity-70`}
    />
    <div className="relative rounded-[2.6rem] bg-slate-900 p-2.5 shadow-2xl">
      <div className="relative rounded-[2.15rem] overflow-hidden bg-white aspect-[9/19.5]">
        {/* Notch */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 bg-slate-900 rounded-full z-20" />
        {children}
      </div>
    </div>
  </div>
);

const AppShowcase = () => {
  return (
    <section
      id="app-showcase"
      data-testid="app-showcase-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[80%] h-[400px] brand-gradient-soft blur-3xl opacity-50" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-flex items-center gap-2 rounded-full bg-white border border-[#EDE9FE] px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#5B21B6]">
            <Smartphone size={14} /> Inside the App
          </span>
          <h2 className="font-display mt-5 text-4xl sm:text-5xl font-black tracking-tighter text-slate-900 leading-[1.05]">
            A peek inside{" "}
            <span className="brand-gradient-text">FinX.</span>
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            From your first tap to your first fill — gradient cards, real
            market stats, and Fifi cheering every order on.
          </p>
        </div>

        {/* 4-up phone grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {SCREENS.map((s, i) => (
            <div
              key={s.title}
              data-testid={`app-screen-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="group flex flex-col"
            >
              <div
                className="transition-transform duration-500 group-hover:-translate-y-2"
                style={{ transform: `rotate(${i % 2 === 0 ? "-2deg" : "2deg"})` }}
              >
                <PhoneFrame accent={s.accent}>
                  <img
                    src={s.src}
                    alt={s.title}
                    className="w-full h-full object-cover object-top"
                  />
                </PhoneFrame>
              </div>
              <div className="mt-6 text-center">
                <span
                  className={`inline-flex items-center gap-1 rounded-full ${s.chipClass} px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em]`}
                >
                  {s.chip}
                </span>
                <h3 className="font-display mt-2 text-lg font-black text-slate-900">
                  {s.title}
                </h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                  {s.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AppShowcase;
